import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Users, Zap, Eye, X } from "lucide-react";
import api from "@/lib/api";
import SegmentFilterBuilder from "./SegmentFilterBuilder";

export default function ListsPanel() {
  const [lists, setLists] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [locations, setLocations] = useState({ countries: [], cities: [] });
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [type, setType] = useState("dynamic");
  const [filter, setFilter] = useState({ mode: "filtered", roles: [], tags: [] });
  const [creating, setCreating] = useState(false);
  const [viewingList, setViewingList] = useState(null);
  const [viewingContacts, setViewingContacts] = useState([]);

  const loadLists = () => {
    api.get("/admin/crm/lists").then((r) => setLists(r.data)).catch(() => toast.error("Could not load lists"));
  };

  useEffect(() => {
    loadLists();
    api.get("/admin/crm/contacts/tags").then((r) => setAllTags(r.data));
    api.get("/admin/crm/contacts/locations").then((r) => setLocations(r.data));
  }, []);

  const createList = async () => {
    if (!name.trim()) { toast.error("List name is required"); return; }
    setCreating(true);
    try {
      await api.post("/admin/crm/lists", { name, type, filter: type === "dynamic" ? filter : undefined, member_ids: [] });
      toast.success("List created");
      setName(""); setFilter({ mode: "filtered", roles: [], tags: [] }); setShowForm(false);
      loadLists();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not create list");
    } finally {
      setCreating(false);
    }
  };

  const deleteList = async (id) => {
    await api.delete(`/admin/crm/lists/${id}`);
    setLists((prev) => prev.filter((l) => l.id !== id));
    toast.success("List deleted");
  };

  const viewList = async (lst) => {
    setViewingList(lst);
    const { data } = await api.get(`/admin/crm/lists/${lst.id}/contacts`);
    setViewingContacts(data.contacts);
  };

  return (
    <div data-testid="crm-lists-panel">
      <div className="flex justify-end mb-4">
        <button onClick={() => setShowForm((s) => !s)} data-testid="crm-add-list-toggle-btn"
          className="flex items-center gap-1.5 text-xs font-semibold bg-primary hover:bg-primary/90 text-white px-3 py-2 rounded-lg">
          <Plus className="w-3.5 h-3.5" /> New List / Segment
        </button>
      </div>

      {showForm && (
        <div className="glass-panel rounded-xl p-4 mb-6" data-testid="crm-list-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="List Name (e.g. Data Cloud Learners)"
              data-testid="crm-list-name-input" className="bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white" />
            <select value={type} onChange={(e) => setType(e.target.value)} data-testid="crm-list-type-select"
              className="bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-sm text-white">
              <option value="dynamic">Dynamic Segment (auto-updates)</option>
              <option value="static">Static List (manual membership)</option>
            </select>
          </div>
          {type === "dynamic" ? (
            <SegmentFilterBuilder filter={filter} setFilter={setFilter} allTags={allTags} allCountries={locations.countries} allCities={locations.cities} />
          ) : (
            <p className="text-xs text-slate-400">Static lists start empty — add contacts to it from the Contacts tab using bulk "Add to List".</p>
          )}
          <button onClick={createList} disabled={creating} data-testid="crm-save-list-btn"
            className="mt-4 bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-300 text-xs font-semibold px-4 py-2 rounded-lg disabled:opacity-50">
            {creating ? "Creating…" : "Create List"}
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {lists.map((lst) => (
          <div key={lst.id} className="glass-panel rounded-xl p-4" data-testid={`crm-list-card-${lst.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-sm font-semibold text-white">{lst.name}</p>
                <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase px-2 py-0.5 rounded-full mt-1 ${lst.type === "dynamic" ? "bg-amber-400/10 text-amber-300" : "bg-indigo-400/10 text-indigo-300"}`}>
                  {lst.type === "dynamic" ? <Zap className="w-3 h-3" /> : <Users className="w-3 h-3" />} {lst.type}
                </span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button onClick={() => viewList(lst)} data-testid={`crm-view-list-btn-${lst.id}`} className="text-slate-400 hover:text-white"><Eye className="w-3.5 h-3.5" /></button>
                <button onClick={() => deleteList(lst.id)} data-testid={`crm-delete-list-btn-${lst.id}`} className="text-red-400 hover:text-red-300"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
            </div>
            <p className="text-xs text-slate-400 mt-2">{lst.member_count} contact(s)</p>
          </div>
        ))}
        {lists.length === 0 && <p className="text-sm text-slate-400 text-center py-8 sm:col-span-2" data-testid="crm-no-lists">No lists yet — create a static list or a dynamic segment.</p>}
      </div>

      {viewingList && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setViewingList(null)} data-testid="crm-list-view-modal">
          <div className="glass-panel rounded-2xl p-5 max-w-lg w-full max-h-[70vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold text-white">{viewingList.name} — {viewingContacts.length} contact(s)</p>
              <button onClick={() => setViewingList(null)} data-testid="crm-close-list-view-btn"><X className="w-4 h-4 text-slate-400 hover:text-white" /></button>
            </div>
            <div className="space-y-1.5">
              {viewingContacts.map((c) => <div key={c.id} className="text-xs text-slate-300 border-b border-white/5 py-1.5">{c.name} — {c.email}</div>)}
              {viewingContacts.length === 0 && <p className="text-xs text-slate-500">No contacts match this list yet.</p>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
